import React from 'react';
import { useVisibleTicker } from '../useVisibleTicker';

/* 各产品品牌色（原为 #4ECDC4）统一收到强调色：
   五个演示在同一页滚动出现，各用自己的色会让强调色失去指示作用。
   SVG 的 stroke/fill 不吃 CSS 变量的回退链，这里取字面值。 */
const BRAND = '#ff6308';

/** 亲密度（0–1）驱动连线的粗细与透明度，是图谱的核心信息维度 */
const PEOPLE = [
  { name: '张伟', tag: '同事', closeness: 0.9 },
  { name: '李娜', tag: '好友', closeness: 0.75 },
  { name: '王芳', tag: '客户', closeness: 0.5 },
  { name: '陈明', tag: '同学', closeness: 0.62 },
  { name: '刘洋', tag: '客户', closeness: 0.38 },
  { name: '赵敏', tag: '家人', closeness: 0.95 },
];

const CENTER = 100;
const ORBIT = 64;

/**
 * 蛐蛐 QuQu · 关系图谱模拟（手机形态）。
 *
 * 用 SVG 而非图片：图谱是这个产品的信息结构本身，
 * 连线粗细直接编码亲密度，静态截图无法表达"关系可被度量"这层含义。
 * 节点错峰浮动让网络显得"活着"，但幅度控制在 2px 内避免喧哗。
 */
const QuQuGraphDemo: React.FC = () => {
  const { ref, tick } = useVisibleTicker(2600);
  // 高亮节点缓慢轮转，模拟"正在提醒你联系某人"
  const highlight = tick % PEOPLE.length;

  const nodes = PEOPLE.map((p, i) => {
    const angle = (i / PEOPLE.length) * Math.PI * 2 - Math.PI / 2;
    return {
      ...p,
      x: CENTER + Math.cos(angle) * ORBIT,
      y: CENTER + Math.sin(angle) * ORBIT,
    };
  });

  return (
    <div ref={ref} className="flex justify-center">
      <div className="sx-device sx-device-phone w-[248px] shrink-0">
        <div
          className="relative rounded-[22px] overflow-hidden flex flex-col"
          style={{ background: '#0b0b0c', aspectRatio: '232 / 400' }}
        >
          {/* 顶部栏 */}
          <div
            className="flex items-center justify-between px-3.5 py-3 shrink-0"
            style={{ borderBottom: '1px solid var(--sx-line)' }}
          >
            <div className="flex items-center gap-1.5">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: BRAND }}
                aria-hidden="true"
              />
              <span className="text-[11px] font-medium text-white">关系图谱</span>
            </div>
            <span className="sx-mono text-[8px]">128 人</span>
          </div>

          {/* 图谱主体 */}
          <div className="flex-1 flex items-center justify-center px-2">
            <svg viewBox="0 0 200 200" className="w-full" aria-hidden="true">
              {/* 同心参考圈：暗示"亲密度分层" */}
              {[34, 52, 70].map((r) => (
                <circle
                  key={r}
                  cx={CENTER}
                  cy={CENTER}
                  r={r}
                  fill="none"
                  stroke="#1f2228"
                  strokeWidth="0.75"
                />
              ))}

              {/* 连线：粗细与透明度由亲密度决定 */}
              {nodes.map((n, i) => (
                <line
                  key={n.name}
                  x1={CENTER}
                  y1={CENTER}
                  x2={n.x}
                  y2={n.y}
                  stroke={BRAND}
                  strokeWidth={0.6 + n.closeness * 1.5}
                  opacity={i === highlight ? 0.85 : 0.16 + n.closeness * 0.22}
                  style={{ transition: 'opacity 0.9s cubic-bezier(0.22,1,0.36,1)' }}
                />
              ))}

              {/* 外围人物节点 */}
              {nodes.map((n, i) => (
                <g
                  key={n.name}
                  className="sx-float-node"
                  style={{ animationDelay: `${i * 0.45}s` }}
                >
                  <circle
                    cx={n.x}
                    cy={n.y}
                    r="15"
                    fill="#131416"
                    stroke={i === highlight ? BRAND : '#26292f'}
                    strokeWidth={i === highlight ? 1.4 : 1}
                    style={{ transition: 'stroke 0.9s cubic-bezier(0.22,1,0.36,1)' }}
                  />
                  <text
                    x={n.x}
                    y={n.y + 3.5}
                    textAnchor="middle"
                    fontSize="9"
                    fill={i === highlight ? '#fff' : '#8b9199'}
                    fontWeight="500"
                  >
                    {n.name[0]}
                  </text>
                </g>
              ))}

              {/* 中心：我 */}
              <circle cx={CENTER} cy={CENTER} r="21" fill={BRAND} />
              <text
                x={CENTER}
                y={CENTER + 4}
                textAnchor="middle"
                fontSize="11"
                fill="#05201e"
                fontWeight="600"
              >
                我
              </text>
            </svg>
          </div>

          {/* 智能提醒：产品把"凭记忆"变成"可管理"的落点 */}
          <div className="px-3 pb-3 shrink-0">
            <div
              className="rounded-[10px] px-2.5 py-2"
              style={{ background: 'rgba(78,205,196,0.08)', border: '1px solid rgba(78,205,196,0.2)' }}
            >
              <p className="sx-mono text-[8px] mb-1" style={{ color: BRAND }}>
                待办提醒
              </p>
              <p className="text-[10px] leading-tight" style={{ color: '#B8E9E5' }}>
                {PEOPLE[highlight].name} · {PEOPLE[highlight].tag} · 已 21 天未联系
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuQuGraphDemo;
